import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiLock, FiZap, FiCamera, FiUploadCloud } = FiIcons;

export default function TrustSpeed() {
  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-900/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-20">
          
          {/* Trust Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center mb-8 border border-slate-700">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-3xl font-bold mb-6">Private & Secure Galleries</h3>
            <ul className="space-y-6">
               <li className="flex items-start gap-4">
                 <SafeIcon icon={FiLock} className="w-6 h-6 text-slate-400 shrink-0 mt-0.5" />
                 <div>
                   <h4 className="font-semibold text-slate-200">Your memories stay yours</h4>
                   <p className="text-slate-400 text-sm mt-1">We don't claim ownership or sell your data. Your event is private.</p>
                 </div>
               </li>
               <li className="flex items-start gap-4">
                 <SafeIcon icon={FiShield} className="w-6 h-6 text-slate-400 shrink-0 mt-0.5" />
                 <div>
                   <h4 className="font-semibold text-slate-200">No spam. No clutter.</h4>
                   <p className="text-slate-400 text-sm mt-1">Guests don't need to create accounts or give up their email just to upload.</p>
                 </div>
               </li>
            </ul>
            
            <div className="mt-10 pt-10 border-t border-slate-800">
              <p className="text-sm text-slate-400 font-medium">TRUSTED BY THOUSANDS OF EVENTS WORLDWIDE</p>
              <div className="flex -space-x-3 mt-4">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-800 overflow-hidden">
                    <img src={`https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80&random=${i+10}`} alt="User" />
                  </div>
                ))}
                <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-300">
                  +2k
                </div>
              </div>
            </div>
          </motion.div>

          {/* Speed Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-800/50 rounded-3xl p-8 md:p-12 border border-slate-700 backdrop-blur-sm"
          >
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-8 border border-blue-500/20">
              <SafeIcon icon={FiZap} className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-3xl font-bold mb-4">From QR Scan to Upload in Seconds</h3>
            <p className="text-slate-400 mb-10">We optimized the upload process so guests spend less time on their phones and more time enjoying the event.</p>
            
            {/* 3-Step Micro Animation Mockup */}
            <div className="flex items-center justify-between relative">
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-700 -translate-y-1/2 z-0" />
              
              <div className="relative z-10 flex flex-col items-center gap-3">
                <motion.div 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="w-14 h-14 rounded-full bg-slate-900 border-2 border-slate-600 flex items-center justify-center"
                >
                  <SafeIcon icon={FiIcons.FiMaximize} className="w-6 h-6 text-slate-300" />
                </motion.div>
                <span className="text-xs font-medium text-slate-400">Scan</span>
              </div>
              
              <div className="relative z-10 flex flex-col items-center gap-3">
                <motion.div 
                  animate={{ y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 2, delay: 0.3 }}
                  className="w-14 h-14 rounded-full bg-slate-900 border-2 border-blue-500 flex items-center justify-center"
                >
                  <SafeIcon icon={FiCamera} className="w-6 h-6 text-blue-400" />
                </motion.div>
                <span className="text-xs font-medium text-blue-400">Snap/Select</span>
              </div>
              
              <div className="relative z-10 flex flex-col items-center gap-3">
                <motion.div 
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2, delay: 0.6 }}
                  className="w-14 h-14 rounded-full bg-slate-900 border-2 border-green-500 flex items-center justify-center"
                >
                   <SafeIcon icon={FiUploadCloud} className="w-6 h-6 text-green-400" />
                </motion.div>
                <span className="text-xs font-medium text-green-400">Upload</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}